// Angular
import { Observable, takeUntil } from 'rxjs';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';

// Libraries
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { isAfter } from 'date-fns';
import * as objectPath from 'object-path';

// Models
import { AppCleanup } from '../../models';
import { HorizontalItem } from '../../models/horizontal-item.model';

// Services
import { ChannelService } from '../../../modules/channel/services';
import { DailyReportService } from '../../../modules/job/components/daily-report/services/daily-report.service';
import { DeviceHub } from '../../../modules/job/services/device.hub';
import { JobHub } from '../../../modules/job/services/job.hub';
import { JobService } from '../../../modules/job/services/job.service';
import { LocationService, NotificationService, SyncService } from '../../services';
import { CoreService } from '../../services/core.service';
import { LayoutConfigService } from '../../services/layout-config.service';
import { UtilityService } from '../../services/utility.service';
import { DynamicSideNavService } from '../dynamic-side-nav/services/dynamic-side-nav.service';
import { HeaderService } from '../header/services/header.service';
import { SpinnerService } from '../spinner/services/spinner.service';

// Components
import { MatDialog } from '@angular/material/dialog';

// Store
import { CommentDataActions } from '../../../modules/comment/state/comment.action';
import { DashboardActions } from '../../../modules/job/components/dashboard/state/dashboard.action';
import { JobActions } from '../../../modules/job/state/job.action';
import { LookupActions } from '../../state/lookup.action';
import { SideNavModel, StorageState } from '../../state/storage.state';

// Enums
import { EntityKeyType } from '../../../modules/job/enums/entity-key-type.enum';
import { StatusType } from '../../enums';
import { RecordChangeSourceType } from '../../enums/record-change-source-type.enum';

// Helpers
import { UtilityHelper } from '../../helpers';
import { StorageActions } from '../../state/storage.action';

// Config
import { RecorChangeType } from '../../../modules/job/enums/record-change-type.enum';
import { PageConfig } from '../../config/page.config';

@Component({
	selector: 'base',
	templateUrl: './base.component.html',
	styleUrls: ['./base.component.scss'],
})
export class BaseComponent extends AppCleanup implements OnInit, AfterViewInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(StorageState.sideNav) sideNav$: Observable<SideNavModel>;
	sideNav: SideNavModel;
	sideNavOpened: boolean = false;
	sideNavCollapsed: boolean = false;
	dynamicSideNavOpened: boolean = false;
	showBackdrop: boolean = false;
	showContentSpinner: boolean;
	pageViews = new PageConfig().pageViews;
	routerUrl: string;
	machineName: string;
	promptedForJobSync: boolean = false;
	isPublic: boolean;
	publicJobGuid: string;
	menuList: HorizontalItem[];
	showProgressBar: boolean = false;
	rightContainerClass: string;
	@ViewChild('header', { static: true }) header: ElementRef;
	primaryColor: string;
	yOffset: number;

	constructor(
		private layoutConfigService: LayoutConfigService,
		private router: Router,
		private dialog: MatDialog,
		private jobService: JobService,
		private jobHub: JobHub,
		private coreService: CoreService,
		private channelService: ChannelService,
		private syncService: SyncService,
		private deviceHub: DeviceHub,
		private dailyReportService: DailyReportService,
		private locationService: LocationService,
		private notificationService: NotificationService,
		private utilityService: UtilityService,
		private translateService: TranslateService,
		private dynamicSideNavService: DynamicSideNavService,
		private headerService: HeaderService,
		private loader: LoadingBarService,
		private store: Store,
		private changeDetectorRef: ChangeDetectorRef,
		private spinnerService: SpinnerService
	) {
		super();

		this.router.events.pipe(takeUntil(this.destroy)).subscribe((value) => {
			if (value instanceof NavigationEnd) {
				this.showContentSpinner = false; // Hides contentn spinner once you navigate away from the page where it was initiated
				// this.store.dispatch(new StorageActions.SetCurrentRoute(value.urlAfterRedirects));
				this.routerUrl = value.urlAfterRedirects;
				this.mapUrl();
			}
		});
	}

	ngOnInit() {
		this.setupLiveEventHandling();

		this.locationService.initLocations();

		this.spinnerService.showContentSpinner$.pipe(takeUntil(this.destroy)).subscribe((showContentSpinner) => {
			this.showContentSpinner = showContentSpinner;
			this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
		});

		this.sideNav$.pipe(takeUntil(this.destroy)).subscribe((sideNav) => {
			if (sideNav) {
				this.sideNav = UtilityHelper.deepCopy(sideNav);
				this.sideNavOpened = this.sideNav.opened;

				if (window.innerWidth < 1200) {
					if (this.sideNav.opened) {
						this.showBackdrop = true;
						document.body.classList.add('overflow-hidden'); // This hides scroll on mobile devices when menu is active
					} else {
						this.showBackdrop = false;
						document.body.classList.remove('overflow-hidden');
					}
				} else {
					this.showBackdrop = false;
					document.body.classList.remove('overflow-hidden');
				}

				this.sideNavCollapsed = this.sideNav.collapsed;
			} else {
				// If no state sate, initialize it
				this.sideNav = new SideNavModel();
				this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
			}

			this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
		});

		this.dynamicSideNavService.opened.pipe(takeUntil(this.destroy)).subscribe((opened) => {
			this.dynamicSideNavOpened = opened;

			if (opened) {
				this.showBackdrop = true;
				document.body.classList.add('overflow-hidden'); // This hides scroll on mobile devices when menu is active
			} else {
				this.showBackdrop = false;
				document.body.classList.remove('overflow-hidden');
			}
		});

		this.headerService.menuList$.pipe(takeUntil(this.destroy)).subscribe((menuList) => {
			if (menuList) {
				// Setting timeout for this error: Expression has changed after it was checked
				setTimeout(() => {
					this.menuList = menuList;
				}, 500);
				//this.cdr.markForCheck();
			}
		});

		// page progress bar percentage
		this.router.events.pipe(takeUntil(this.destroy)).subscribe((event) => {
			if (event instanceof NavigationStart) {
				// set page progress bar loading to start on NavigationStart event router
				this.showProgressBar = true;
				this.loader.start();
				this.loader.increment(0);
			}

			if (event instanceof RouteConfigLoadStart) {
				this.loader.increment(25);
			}
			if (event instanceof RouteConfigLoadEnd) {
				this.loader.increment(65);
			}
			if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
				// set page progress bar loading to end on NavigationEnd event router

				setTimeout(() => {
					// This gives desired effect. Loading spinner doesn't flicker fast and stays visible for long enough
					this.loader.increment(100);
					this.loader.complete();
					this.showProgressBar = false;
				}, 500);
			}
		});
	}

	ngAfterViewInit() {
		// keep header element in the service
		this.layoutConfigService.localLayoutConfig$.pipe(takeUntil(this.destroy)).subscribe((layoutConfig) => {
			this.primaryColor = objectPath.get(layoutConfig, 'loader.color');
			this.setProgressBarColor();
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	private mapUrl() {
		let index = this.routerUrl.toLowerCase().indexOf('/job/link/');

		if (index >= 0) {
			this.isPublic = true;
			this.publicJobGuid = this.routerUrl.toLowerCase().substr(index + 10, 36);
		}
	}

	setupLiveEventHandling() {
		this.jobHub.recordChange$.pipe(takeUntil(this.destroy)).subscribe((result) => {
			// console.log('by Isaac recordChange', result);
			if (result?.entityKey) {
				if ((this.isViewer && result.source === RecordChangeSourceType.LOCAL_VIEWER) || (!this.isViewer && result.source === RecordChangeSourceType.PORTAL)) {
					if (result.entityKey.toLowerCase() === EntityKeyType.JOB) {
						if (this.publicJobGuid) {
							this.jobService.refreshPublicJob(this.publicJobGuid);
						} else {
							this.jobService.refreshJob(result.key, true, false);
						}

						if (!this.publicJobGuid && result.recordChangeType === RecorChangeType.DELETE) {
							this.store.dispatch(new JobActions.GetAllCompletedJobs()); // When job is deleted, refresh completed jobs
						}
					} else if (result.entityKey.toLowerCase() === EntityKeyType.JOB_COMMENT || result.entityKey.toLowerCase() === EntityKeyType.JOB_COMMENT_READY || result.entityKey.toLowerCase() === EntityKeyType.JOB_COMMENTS) {
						if (this.publicJobGuid) {
							this.jobService.refreshPublicJobComments(result.key, this.publicJobGuid);
						} else {
							this.jobService.refreshJobComments(result.key);
						}
					} else if (!this.publicJobGuid && result.entity && result.entityKey.toLowerCase() === EntityKeyType.CHANNEL) {
						this.channelService.modifyChannelRecord(result.entity, false);
					} else if (!this.publicJobGuid && result.entityKey.toLowerCase().startsWith('jobcommentdefault')) {
						this.store.dispatch(new CommentDataActions.GetAll({ showSpinner: false, showToast: false }));
					} else if (!this.publicJobGuid && result.entity && result.entityKey.toLowerCase() === EntityKeyType.JOB_DAILY_REPORT) {
						this.dailyReportService.modifyDailyReportRecord(result.key, result.entity, true);
					} else if (!this.publicJobGuid && result.entity && result.entityKey.toLowerCase() === EntityKeyType.JOB_CHANNEL_OFFSET) {
						this.jobService.getUnitPeriodChannelOffsets(result.key);
					} else if (!this.publicJobGuid && result.entityKey.toLowerCase() === EntityKeyType.UNIT_PERIOD_CHANNELS) {
						this.jobService.getUnitPeriodChannels(result.key);
					} else if (!this.publicJobGuid && result.entity && result.entityKey.toLowerCase() === EntityKeyType.SYNC) {
						this.syncService.triggerSyncMessage(result.key, result.entity, false, false);
					} else if (!this.publicJobGuid && result.entity && result.entityKey.toLowerCase() === EntityKeyType.HISTORICAL_QUEUE) {
						this.jobService.refreshJob(result.entity.fromJobId, true, false, 'splitMerge');
						this.notificationService.updateNotification(result.entity);
					}

					// All SignalR Triggers Should Also Trigger a Sync for Local Viewer
					if (this.isViewer && result.source === 'local-viewer') {
						if (this.jobService.currentJob) {
							this.syncService.syncJob(this.jobService.currentJob, 'latest', false, false);
						} else if (result.key) {
							this.syncService.syncJobWithKey(result.key, 'latest', false, false);
						}
					}
				} else if (this.isViewer) {
					if (!this.machineName) this.getMachineName();
					// Anything that was sent from the Portal to the Local Viewer is sent specifically to trigger a sync.
					// No logic need, always sync
					this.syncService.syncJob(this.jobService.currentJob, 'latest', false, false);
				}

				if (result.entityKey.toLowerCase() === EntityKeyType.SYNC) {
					// Moving this here from subheader
					let latestSyncDate: any = this.syncService.getLatestSyncDate().getValue();
					let newLatestSyncDate = result.entity.syncDate;

					if (latestSyncDate) {
						// If date is already set
						latestSyncDate = new Date(latestSyncDate);
						newLatestSyncDate = new Date(newLatestSyncDate);

						if (isAfter(newLatestSyncDate, latestSyncDate)) {
							this.syncService.setLatestSyncDate(newLatestSyncDate);
						}
					} else {
						// If date is not already set
						this.syncService.setLatestSyncDate(newLatestSyncDate);
					}
				}
			}
		});

		if (this.isViewer) {
			this.getMachineName();

			this.utilityService
				.isOnline()
				.pipe(takeUntil(this.destroy))
				.subscribe((isConnected) => {
					if (isConnected) {
						this.deviceHub.subscribeToDevice();
						if (this.jobService.currentJob) this.syncService.syncJob(this.jobService.currentJob, 'latest', true, true);
					} else {
					}

					this.syncService.setConnection(isConnected);
				});

			this.syncService.currentJobSynced$.pipe(takeUntil(this.destroy)).subscribe((currentJobSynced) => {
				// console.log('by Isaac currentJobSync viewer', currentJobSynced);
				if (currentJobSynced && currentJobSynced.syncResponse) {
					if (currentJobSynced.syncResponse.syncMessage === 'final-sync') {
						// Let it sit in Completed Jobs and give some time for user to see it complete
						setTimeout(() => {
							this.store.dispatch(new JobActions.GetAllActiveJobs());
							this.jobService.sendJobDeletedEvent(currentJobSynced.key);
						}, 5000);
					} else if (currentJobSynced.syncResponse.syncMessage === 'site-not-available') {
						currentJobSynced.showToaster = false;
						const message = `${this.translateService.instant('GENERIC.SYNC_FAILED_PORTAL_UNREACHABLE')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					} else if (currentJobSynced.syncResponse.syncMessage === 'retrieve-failed') {
						currentJobSynced.showToaster = false;
						const message = `${this.translateService.instant('GENERIC.SYNC_FAILED_TO_RETRIEVE_DATA')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					} else if (currentJobSynced.syncResponse.syncMessage === 'job-on-temporaryunit') {
						currentJobSynced.showToaster = false;
						const message = `${this.translateService.instant('GENERIC.SYNC_FAILED_TEMPORARY_UNIT')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					} else if (currentJobSynced.syncResponse.syncMessage === 'send-failed') {
						currentJobSynced.showToaster = false;
						const message = `${this.translateService.instant('GENERIC.SYNC_FAILED_TO_RETRIEVE_DATA')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					} else if (currentJobSynced.syncResponse.syncMessage === 'job-no-longer-exists') {
						currentJobSynced.showToaster = false;
						this.jobService.sendJobDeletedEvent(currentJobSynced.key);
					} else {
						if (
							currentJobSynced.syncResponse.job ||
							currentJobSynced.syncResponse.unitPeriods ||
							currentJobSynced.syncResponse.jobComments ||
							currentJobSynced.syncResponse.jobDailyReports ||
							currentJobSynced.syncResponse.jobDailyReportHeaderValues
						) {
							this.jobService
								.getJob(currentJobSynced.key, true, false)
								.pipe(takeUntil(this.destroy))
								.subscribe((_) => {
									if (currentJobSynced.syncResponse.jobDailyReports || currentJobSynced.syncResponse.jobDailyReportHeaderValues) this.dailyReportService.getJobDailyReports(currentJobSynced.key);
								});
						}

						if (currentJobSynced.syncResponse.jobScopes) {
							this.store.dispatch(new LookupActions.GetAll());
						}

						if (currentJobSynced.syncResponse.dashboards) {
							if (this.jobService.currentJob) {
								this.store.dispatch(
									new DashboardActions.GetAll({
										jobKey: this.jobService.currentJob.key,
										showSpinner: false,
										showToast: false,
									})
								);
							}
						}
					}

					if (currentJobSynced.showToaster) {
						const message = `Job ${this.translateService.instant('GENERIC.HAS_SYNCED')}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}

					this.jobService.modifyJobSyncDate(currentJobSynced.key, currentJobSynced.syncResponse.syncDate);
				}
			});
		} else {
			this.syncService.currentJobSynced$.pipe(takeUntil(this.destroy)).subscribe((currentJobSynced) => {
				// console.log('by Isaac currentJobSync portal', currentJobSynced);
				if (currentJobSynced && currentJobSynced.syncResponse) {
					if (
						currentJobSynced.syncResponse.job ||
						currentJobSynced.syncResponse.unitPeriods ||
						currentJobSynced.syncResponse.unitPeriodChannels ||
						currentJobSynced.syncResponse.unitPeriodChannelOffsets ||
						currentJobSynced.syncResponse.jobComments ||
						currentJobSynced.syncResponse.jobDailyReports ||
						currentJobSynced.syncResponse.jobDailyReportHeaderValues
					) {
						this.jobService
							.getJob(currentJobSynced.key, true, false)
							.pipe(takeUntil(this.destroy))
							.subscribe(() => {
								if (currentJobSynced.syncResponse.jobDailyReports || currentJobSynced.syncResponse.jobDailyReportHeaderValues) this.dailyReportService.getJobDailyReports(currentJobSynced.key);
							});
					}

					if (currentJobSynced.showToaster) {
						const message = `Job ${this.translateService.instant('GENERIC.HAS_SYNCED')}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				}
			});
		}
	}

	getMachineName() {
		this.syncService
			.getMachineName()
			.pipe(takeUntil(this.destroy))
			.subscribe((device) => {
				if (device?.machineName) {
					this.machineName = device.machineName;
					this.deviceHub.initHub(device.machineName);
				}
			});
	}

	setProgressBarColor() {
		let id = 'progress-bar';
		let style = `
			.mat-mdc-progress-bar {
				--mdc-linear-progress-active-indicator-color: ${this.primaryColor} !important; //progress bar color
				--mdc-linear-progress-track-color: ${this.primaryColor} !important; // background color
			}
		`;
		UtilityHelper.insertStyleTagDom(id, style);
	}

	onScroll(event) {
		this.yOffset = event.srcElement.scrollTop;
	}
}
